/**
 * Bales Runout Report — for GM, QC, Plant Head, MD review.
 *
 * A mill lot 'runs out' when its last bale is consumed for mixing.
 * This page lists every runout lot with the deltas between PURCHASE
 * (gross weight + moisture at receipt) and ACTUAL CONSUMPTION (sum of
 * scanned weights at feed point + tare deductions).
 *
 * Columns + KPIs are tuned for a senior reviewer skimming for outliers:
 *
 *   - Realisation % < 95%    → 🔴 likely supplier moisture overload
 *   - Weight loss % > 3%     → 🔴 godown leakage / weighbridge drift
 *   - Moisture drop > 1.5%   → ⚠ wet bales dried in storage
 *
 * Click any row → drawer with bale-by-bale breakdown + print button.
 *
 * Source: GET /bale-mgmt/runout-lots
 *         GET /bale-mgmt/runout-lots/{lot_id}/report
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Card, Col, DatePicker, Drawer, Empty, Row, Space, Spin,
  Statistic, Table, Tag, Typography, Button,
} from 'antd'
import {
  ArrowDownOutlined, CheckCircleOutlined, ExperimentOutlined,
  PrinterOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title } = Typography
const { RangePicker } = DatePicker

interface RunoutSummary {
  lot_id: string
  mill_lot_no: string
  supplier_name: string
  cotton_variety: string | null
  crop_year: string | null
  receipt_date: string | null
  last_consumed_at: string | null
  total_bales: number
  approved_bales: number
  lines_count: number
  purchase_gross_kg: number
  purchase_moisture_pct: number | null
  actual_gross_kg: number
  actual_moisture_pct: number | null
  tare_kg: number
  net_consumed_kg: number
  weight_loss_kg: number
  weight_loss_pct: number | null
  moisture_diff_pct: number | null
  realisation_pct: number | null
  anomaly_flags: string[]
  landed_cost_per_kg: number | null
  purchase_value: number | null
}

interface BaleBreakdownRow {
  bale_no: string
  purchase_weight_kg: number | null
  purchase_moisture_pct: number | null
  actual_weight_kg: number | null
  actual_moisture_pct: number | null
  weight_diff_kg: number | null
  tare_kg: number | null
  scan_timestamp: string | null
  laydown_no: string | null
}

interface RunoutDetail extends RunoutSummary {
  bales_breakdown: BaleBreakdownRow[]
  generated_at: string
}

const fmt = (n?: number | null, dp = 2) =>
  n != null
    ? n.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : '—'

const PRINT_STYLES = `
@media print {
  body * { visibility: hidden; }
  #runout-printable, #runout-printable * { visibility: visible; }
  #runout-printable {
    position: absolute; left: 0; top: 0;
    width: 100%; padding: 8mm;
  }
  .no-print { display: none !important; }
  @page { size: A4 landscape; margin: 8mm; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`

function realisationColor(pct: number | null | undefined): string {
  if (pct == null) return '#8c8c8c'
  if (pct < 95) return '#f5222d'    // red
  if (pct < 97) return '#fa8c16'    // orange
  return '#52c41a'                   // green
}

export default function BalesRunoutReportPage() {
  const { plantId } = usePlantId()
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [drillLotId, setDrillLotId] = useState<string | null>(null)

  // Inject print CSS once
  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.id = 'runout-print-styles'
    styleEl.innerHTML = PRINT_STYLES
    document.head.appendChild(styleEl)
    return () => { document.head.removeChild(styleEl) }
  }, [])

  const { data: rows = [], isLoading } = useQuery<RunoutSummary[]>({
    queryKey: [
      'runout-lots', plantId,
      range ? range[0].format('YYYY-MM-DD') : null,
      range ? range[1].format('YYYY-MM-DD') : null,
    ],
    queryFn: () =>
      apiClient.get('/bale-mgmt/runout-lots', {
        params: {
          plant_id: plantId,
          from_date: range ? range[0].format('YYYY-MM-DD') : undefined,
          to_date: range ? range[1].format('YYYY-MM-DD') : undefined,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: detail } = useQuery<RunoutDetail>({
    queryKey: ['runout-detail', drillLotId],
    queryFn: () =>
      apiClient.get(`/bale-mgmt/runout-lots/${drillLotId}/report`).then(r => r.data),
    enabled: !!drillLotId,
  })

  // Aggregate KPIs across visible runout lots
  const kpi = useMemo(() => {
    const totalLots = rows.length
    const totalBales = rows.reduce((s, r) => s + (r.total_bales || 0), 0)
    const totalPurchase = rows.reduce((s, r) => s + (r.purchase_gross_kg || 0), 0)
    const totalActual = rows.reduce((s, r) => s + (r.actual_gross_kg || 0), 0)
    const totalNet = rows.reduce((s, r) => s + (r.net_consumed_kg || 0), 0)
    const totalLoss = totalPurchase - totalActual
    const lossPct = totalPurchase > 0 ? (totalLoss / totalPurchase) * 100 : 0
    const realisation = totalPurchase > 0 ? (totalNet / totalPurchase) * 100 : 0
    const anomalyCount = rows.filter(r => r.anomaly_flags.length > 0).length
    return {
      totalLots, totalBales, totalPurchase, totalActual, totalNet,
      totalLoss, lossPct, realisation, anomalyCount,
    }
  }, [rows])

  return (
    <div>
      <PageHeader
        title="Bales Runout Report"
        subtitle="Lot-by-lot purchase-vs-consumption deltas. Generated automatically when a lot's last bale is fed. Review anomalies in red — supplier moisture, weighbridge drift, godown leakage."
        extra={
          <Space>
            <RangePicker
              value={range as any}
              onChange={(v) => setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)}
              format="DD-MMM-YYYY"
              placeholder={['Receipt from', 'Receipt to']}
              allowClear
            />
          </Space>
        }
      />

      {/* ── KPI tiles ─────────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic
              title="Runout Lots"
              value={kpi.totalLots}
              valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 700 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: '3px solid #722ed1' }}>
            <Statistic
              title="Total Bales"
              value={kpi.totalBales}
              valueStyle={{ fontSize: 22, color: '#722ed1', fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: `3px solid ${realisationColor(kpi.realisation)}` }}>
            <Statistic
              title="Avg Realisation %"
              value={fmt(kpi.realisation, 2)}
              suffix="%"
              valueStyle={{
                fontSize: 22, fontWeight: 700,
                color: realisationColor(kpi.realisation),
              }}
              prefix={<ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderTop: `3px solid ${kpi.anomalyCount > 0 ? '#f5222d' : '#52c41a'}` }}>
            <Statistic
              title="Lots With Anomalies"
              value={kpi.anomalyCount}
              suffix={`/ ${kpi.totalLots}`}
              valueStyle={{
                fontSize: 22, fontWeight: 700,
                color: kpi.anomalyCount > 0 ? '#f5222d' : '#52c41a',
              }}
              prefix={kpi.anomalyCount > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Aggregate weight totals */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24} md={6}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total purchased</Text>
              <Text strong style={{ fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(kpi.totalPurchase, 0)} kg
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={6}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total consumed (gross)</Text>
              <Text strong style={{ fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(kpi.totalActual, 0)} kg
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={6}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total weight loss</Text>
              <Text strong style={{ fontSize: 16, color: '#fa8c16', fontVariantNumeric: 'tabular-nums' }}>
                {fmt(kpi.totalLoss, 0)} kg ({fmt(kpi.lossPct, 2)}%)
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={6}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Net cotton in mix</Text>
              <Text strong style={{ fontSize: 16, color: '#52c41a', fontVariantNumeric: 'tabular-nums' }}>
                {fmt(kpi.totalNet, 0)} kg
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Main table ────────────────────────────────────────────── */}
      <Card size="small">
        {!plantId ? <Empty description="Select a plant" /> :
          rows.length === 0 && !isLoading ? (
            <Empty description="No lots have run out yet in this window. Lots appear here when their last bale is consumed for mixing." />
          ) : (
            <Table
              size="small"
              rowKey="lot_id"
              loading={isLoading}
              dataSource={rows}
              pagination={{ pageSize: 25, showTotal: t => `${t} runout lots` }}
              scroll={{ x: 1500 }}
              onRow={(r) => ({
                onClick: () => setDrillLotId(r.lot_id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: 'Mill Lot', dataIndex: 'mill_lot_no', width: 130, fixed: 'left' as const,
                  render: (v: string, r: RunoutSummary) => (
                    <Space direction="vertical" size={0}>
                      <Text code strong style={{ fontSize: 11 }}>{v}</Text>
                      {r.anomaly_flags.length > 0 && (
                        <Tag color="red" style={{ margin: 0, fontSize: 9 }}>
                          {r.anomaly_flags.length} flag{r.anomaly_flags.length > 1 ? 's' : ''}
                        </Tag>
                      )}
                    </Space>
                  ),
                },
                {
                  title: 'Supplier · Variety', key: 'sv', width: 200,
                  render: (_: unknown, r: RunoutSummary) => (
                    <Space direction="vertical" size={0}>
                      <Text style={{ fontSize: 12 }}>{r.supplier_name || '—'}</Text>
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        {r.cotton_variety || '—'} · {r.crop_year || '—'}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: 'Receipt', dataIndex: 'receipt_date', width: 100,
                  render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
                },
                {
                  title: 'Run-out', dataIndex: 'last_consumed_at', width: 110,
                  render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
                },
                {
                  title: 'Bales', dataIndex: 'total_bales', width: 70, align: 'right' as const,
                  render: (v: number) => <Text strong>{v}</Text>,
                },
                {
                  title: 'Purchase kg', dataIndex: 'purchase_gross_kg', width: 110, align: 'right' as const,
                  render: (v: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v, 0)}</Text>,
                },
                {
                  title: 'Actual kg', dataIndex: 'actual_gross_kg', width: 110, align: 'right' as const,
                  render: (v: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v, 0)}</Text>,
                },
                {
                  title: 'Loss kg / %', key: 'loss', width: 110, align: 'right' as const,
                  render: (_: unknown, r: RunoutSummary) => (
                    <Text style={{
                      color: (r.weight_loss_pct ?? 0) > 3 ? '#f5222d'
                            : (r.weight_loss_pct ?? 0) > 1.5 ? '#fa8c16'
                            : '#8c8c8c',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmt(r.weight_loss_kg, 0)} / {fmt(r.weight_loss_pct, 2)}%
                    </Text>
                  ),
                },
                {
                  title: 'Purchase Moist %', dataIndex: 'purchase_moisture_pct', width: 100, align: 'right' as const,
                  render: (v: number | null) => fmt(v, 2),
                },
                {
                  title: 'Actual Moist %', dataIndex: 'actual_moisture_pct', width: 100, align: 'right' as const,
                  render: (v: number | null) => fmt(v, 2),
                },
                {
                  title: 'Δ Moist', dataIndex: 'moisture_diff_pct', width: 90, align: 'right' as const,
                  render: (v: number | null) =>
                    v != null
                      ? <Text style={{
                          color: v > 1.5 ? '#fa8c16' : v < -0.5 ? '#1677ff' : '#8c8c8c',
                          fontVariantNumeric: 'tabular-nums',
                        }}>{v > 0 ? '+' : ''}{fmt(v, 2)}%</Text>
                      : '—',
                },
                {
                  title: 'Realisation %', dataIndex: 'realisation_pct', width: 110, align: 'right' as const,
                  fixed: 'right' as const,
                  render: (v: number | null) =>
                    v != null
                      ? <Text strong style={{
                          color: realisationColor(v),
                          fontSize: 14, fontVariantNumeric: 'tabular-nums',
                        }}>{fmt(v, 2)}%</Text>
                      : '—',
                },
              ]}
            />
          )}
      </Card>

      {/* ── Detail drawer ─────────────────────────────────────────── */}
      <Drawer
        title={detail ? (
          <Space>
            <ExperimentOutlined />
            <Text strong>{detail.mill_lot_no}</Text>
            <Tag>Bales Runout Report</Tag>
          </Space>
        ) : 'Loading…'}
        open={!!drillLotId}
        onClose={() => setDrillLotId(null)}
        width={1100}
        extra={detail && (
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={() => window.print()}
          >
            Print
          </Button>
        )}
      >
        {!detail ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
          <div id="runout-printable">
            {/* Header band */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                <tr>
                  <td style={{
                    border: '2px solid #000', padding: '8px 12px',
                    width: '34%', textAlign: 'center', fontWeight: 700, fontSize: 14,
                  }}>
                    {detail.supplier_name}
                  </td>
                  <td style={{
                    border: '2px solid #000', padding: '8px 12px',
                    textAlign: 'center', fontWeight: 800, fontSize: 18,
                  }}>
                    BALES RUNOUT REPORT
                  </td>
                  <td style={{
                    border: '2px solid #000', padding: '8px 12px',
                    width: '20%', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 10 }}>RUN-OUT DATE</div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {detail.last_consumed_at ? dayjs(detail.last_consumed_at).format('DD-MM-YYYY') : '—'}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Lot info */}
            <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '8px 12px' } }}>
              <Row gutter={[12, 4]}>
                <Col xs={12} md={6}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Mill Lot No</Text>
                  <div><Text code strong>{detail.mill_lot_no}</Text></div>
                </Col>
                <Col xs={12} md={6}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Variety</Text>
                  <div><Text strong>{detail.cotton_variety || '—'}</Text></div>
                </Col>
                <Col xs={12} md={6}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Crop Year</Text>
                  <div><Text strong>{detail.crop_year || '—'}</Text></div>
                </Col>
                <Col xs={12} md={6}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Receipt Date</Text>
                  <div><Text strong>
                    {detail.receipt_date ? dayjs(detail.receipt_date).format('DD-MMM-YYYY') : '—'}
                  </Text></div>
                </Col>
              </Row>
            </Card>

            {/* Anomaly banner */}
            {detail.anomaly_flags.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${detail.anomaly_flags.length} anomal${detail.anomaly_flags.length === 1 ? 'y' : 'ies'} detected`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {detail.anomaly_flags.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                }
              />
            )}

            {/* Purchase vs Actual side-by-side */}
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  style={{ borderLeft: '3px solid #1677ff' }}
                  title={<Text strong>AT RECEIPT (Purchase)</Text>}
                >
                  <Row gutter={[8, 4]}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Total Bales</Text>
                      <div><Text strong style={{ fontSize: 18 }}>{detail.total_bales}</Text></div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Approved</Text>
                      <div><Text strong style={{ fontSize: 18, color: '#52c41a' }}>{detail.approved_bales}</Text></div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Total Weight</Text>
                      <div>
                        <Text strong style={{ fontSize: 18, color: '#1677ff', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(detail.purchase_gross_kg, 0)} kg
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Avg Moisture</Text>
                      <div>
                        <Text strong style={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(detail.purchase_moisture_pct, 2)}%
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  style={{ borderLeft: '3px solid #52c41a' }}
                  title={<Text strong>AT CONSUMPTION (Actual)</Text>}
                >
                  <Row gutter={[8, 4]}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Bales Consumed</Text>
                      <div><Text strong style={{ fontSize: 18 }}>{detail.lines_count}</Text></div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Tare Removed</Text>
                      <div>
                        <Text strong style={{ fontSize: 18, color: '#fa8c16', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(detail.tare_kg, 1)} kg
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Total Actual Wt</Text>
                      <div>
                        <Text strong style={{ fontSize: 18, color: '#1677ff', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(detail.actual_gross_kg, 0)} kg
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Avg Actual Moist</Text>
                      <div>
                        <Text strong style={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(detail.actual_moisture_pct, 2)}%
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* Deltas + Realisation banner */}
            <Card
              size="small"
              style={{ marginBottom: 12, background: '#f6ffed', borderColor: '#b7eb8f' }}
              title={<Text strong>DELTAS — Purchase vs Actual</Text>}
            >
              <Row gutter={[12, 8]}>
                <Col xs={12} md={6}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Weight Loss</Text>
                    <Text strong style={{
                      fontSize: 16, fontVariantNumeric: 'tabular-nums',
                      color: (detail.weight_loss_pct ?? 0) > 3 ? '#f5222d'
                            : (detail.weight_loss_pct ?? 0) > 1.5 ? '#fa8c16' : '#8c8c8c',
                    }}>
                      {fmt(detail.weight_loss_kg, 0)} kg
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({fmt(detail.weight_loss_pct, 2)}%)
                    </Text>
                  </Space>
                </Col>
                <Col xs={12} md={6}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Moisture Drop</Text>
                    <Text strong style={{
                      fontSize: 16, fontVariantNumeric: 'tabular-nums',
                      color: (detail.moisture_diff_pct ?? 0) > 1.5 ? '#fa8c16' : '#8c8c8c',
                    }}>
                      {detail.moisture_diff_pct != null
                        ? `${detail.moisture_diff_pct > 0 ? '+' : ''}${fmt(detail.moisture_diff_pct, 2)}%`
                        : '—'}
                    </Text>
                  </Space>
                </Col>
                <Col xs={12} md={6}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Net Cotton in Mix</Text>
                    <Text strong style={{ fontSize: 16, color: '#52c41a', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(detail.net_consumed_kg, 0)} kg
                    </Text>
                  </Space>
                </Col>
                <Col xs={12} md={6}>
                  <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Realisation</Text>
                    <Text strong style={{
                      fontSize: 22, fontVariantNumeric: 'tabular-nums',
                      color: realisationColor(detail.realisation_pct),
                    }}>
                      {fmt(detail.realisation_pct, 2)}%
                    </Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Bale-by-bale breakdown */}
            <Card
              size="small"
              title={<Text strong>BALE-BY-BALE BREAKDOWN</Text>}
              extra={
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {detail.bales_breakdown.length} bales · click row to spot outliers
                </Text>
              }
            >
              <Table
                size="small"
                pagination={{ pageSize: 50, simple: true }}
                rowKey="bale_no"
                dataSource={detail.bales_breakdown}
                scroll={{ x: 1100 }}
                columns={[
                  { title: 'Bale No', dataIndex: 'bale_no', width: 130, fixed: 'left' as const,
                    render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
                  { title: 'Laydown', dataIndex: 'laydown_no', width: 110,
                    render: (v: string) => v ? <Text code style={{ fontSize: 10 }}>{v}</Text> : '—' },
                  { title: 'Purchase Wt', dataIndex: 'purchase_weight_kg', width: 100, align: 'right' as const,
                    render: (v: number | null) => fmt(v, 1) },
                  { title: 'Actual Wt', dataIndex: 'actual_weight_kg', width: 100, align: 'right' as const,
                    render: (v: number | null) => v != null ? fmt(v, 1) : <Text type="secondary">—</Text> },
                  { title: 'Δ Wt', dataIndex: 'weight_diff_kg', width: 80, align: 'right' as const,
                    render: (v: number | null) =>
                      v != null
                        ? <Text style={{
                            color: v > 5 ? '#f5222d' : v < -2 ? '#1677ff' : '#8c8c8c',
                            fontVariantNumeric: 'tabular-nums',
                          }}>{v > 0 ? '+' : ''}{fmt(v, 1)}</Text>
                        : '—' },
                  { title: 'Tare', dataIndex: 'tare_kg', width: 80, align: 'right' as const,
                    render: (v: number | null) => v != null ? fmt(v, 2) : '—' },
                  { title: 'Pur Moist', dataIndex: 'purchase_moisture_pct', width: 90, align: 'right' as const,
                    render: (v: number | null) => fmt(v, 2) },
                  { title: 'Act Moist', dataIndex: 'actual_moisture_pct', width: 90, align: 'right' as const,
                    render: (v: number | null) => v != null ? fmt(v, 2) : '—' },
                  { title: 'Scanned', dataIndex: 'scan_timestamp', width: 130,
                    render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—' },
                ]}
              />
            </Card>

            <div style={{ marginTop: 8, fontSize: 10, color: '#999', textAlign: 'right' }}>
              Generated: {dayjs(detail.generated_at).format('DD-MMM-YYYY HH:mm')} —
              erp.unitechspin.com
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}
